/**
 * Foot pedal and camera button (imaging-capture.md 3.1 step 5).
 *
 * - Keyboard-type pedals and camera buttons (most USB pedals are HID
 *   keyboards sending F9, F12, Space or Enter): a `keydown` listener with the
 *   learned key, active only while the capture surface is open, with
 *   `preventDefault` so the key does nothing else.
 * - Joystick-type pedals: the Gamepad API, polled each animation frame, one
 *   learned button index (rising edge only).
 * - Vendor HID buttons that are neither: WebHID (`navigator.hid`), any report
 *   that goes from all-zero to non-zero counts as a press. Chromium only, and
 *   it needs a user gesture to pick the device once.
 *
 * "Press your pedal now" (`learnTrigger`) stores the binding per workstation
 * (`velora.capture.pedal.<workstation>`). Without a learned binding, Space
 * on the capture surface still captures.
 *
 * UVC camera buttons are not keyboards: the driver reports them to Windows
 * only. Velora Capture Helper (OpenImaging `VeloraCaptureHelper.exe`, helper.ts)
 * turns a press into `CAMERA_BUTTON_KEY`, which the capture surface always
 * takes as a capture, even with focus in the camera picker.
 */
import { readJson, writeStore } from './workstation.js';

export type PedalBinding =
  | { kind: 'key'; code: string; key: string }
  | { kind: 'gamepad'; button: number; gamepadId: string | null }
  | { kind: 'hid'; vendorId: number; productId: number; name: string };

/** The key the camera button helper sends (no keyboard has it, so nothing else reacts). */
export const CAMERA_BUTTON_KEY = 'F13';

export const pedalKey = (workstation: string) => `velora.capture.pedal.${workstation}`;

export function recallPedal(workstation: string): PedalBinding | null {
  const b = readJson<PedalBinding>(pedalKey(workstation));
  if (!b || typeof b !== 'object') return null;
  if (b.kind === 'key' && typeof b.code === 'string') return b;
  if (b.kind === 'gamepad' && Number.isInteger(b.button)) return b;
  if (b.kind === 'hid' && Number.isInteger(b.vendorId) && Number.isInteger(b.productId)) return b;
  return null;
}

export function rememberPedal(workstation: string, b: PedalBinding | null): void {
  writeStore(pedalKey(workstation), b ? JSON.stringify(b) : null);
}

/** Human label for the binding ("F9", "Gamepad button 2", "HID: Pedal"). */
export function pedalLabel(b: PedalBinding | null): string {
  if (!b) return 'Not set';
  if (b.kind === 'key')
    return b.key === ' ' ? 'Space' : b.key.length === 1 ? b.key.toUpperCase() : b.key;
  if (b.kind === 'gamepad') return `Pedal button ${b.button + 1}`;
  return `USB button (${b.name})`;
}

/** Keys that are never learned as a pedal (they drive the dialog). */
const NOT_LEARNABLE = new Set(['Escape', 'Tab', 'Shift', 'Control', 'Alt', 'Meta', 'CapsLock']);

export function matchesKey(
  b: PedalBinding | null,
  e: Pick<KeyboardEvent, 'code' | 'key'>,
): boolean {
  return b?.kind === 'key' && (e.code === b.code || (!e.code && e.key === b.key));
}

type GamepadsFn = () => readonly (Gamepad | null)[];
type Raf = (cb: () => void) => number;
type Caf = (id: number) => void;

export interface TriggerEnv {
  target: Pick<Window, 'addEventListener' | 'removeEventListener'>;
  getGamepads: GamepadsFn | null;
  raf: Raf;
  caf: Caf;
  hid: HidLike | null;
}

interface HidDeviceLike {
  vendorId: number;
  productId: number;
  productName: string;
  opened: boolean;
  open(): Promise<void>;
  addEventListener(type: 'inputreport', l: (e: { data: DataView }) => void): void;
  removeEventListener(type: 'inputreport', l: (e: { data: DataView }) => void): void;
}
export interface HidLike {
  getDevices(): Promise<HidDeviceLike[]>;
  requestDevice(opts: { filters: object[] }): Promise<HidDeviceLike[]>;
}

export function browserTriggerEnv(): TriggerEnv {
  const w = globalThis as unknown as Window & { navigator: Navigator & { hid?: HidLike } };
  return {
    target: w,
    getGamepads:
      typeof w.navigator?.getGamepads === 'function' ? () => w.navigator.getGamepads() : null,
    raf: (cb) => w.requestAnimationFrame(cb),
    caf: (id) => w.cancelAnimationFrame(id),
    hid: w.navigator?.hid ?? null,
  };
}

const editable = (t: EventTarget | null) => {
  const el = t as HTMLElement | null;
  return !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName ?? ''));
};

/**
 * Listens for the bound pedal and calls `onPress` once per press. Returns
 * the cleanup. Keys typed into an input never trigger a capture.
 */
export function createTrigger(
  binding: PedalBinding | null,
  onPress: () => void,
  env: TriggerEnv = browserTriggerEnv(),
): () => void {
  if (!binding) return () => undefined;
  const cleanups: (() => void)[] = [];

  if (binding.kind === 'key') {
    const onKey = (ev: Event) => {
      const e = ev as KeyboardEvent;
      if (e.repeat || editable(e.target) || !matchesKey(binding, e)) return;
      e.preventDefault();
      e.stopPropagation();
      onPress();
    };
    env.target.addEventListener('keydown', onKey, true);
    cleanups.push(() => env.target.removeEventListener('keydown', onKey, true));
  }

  if (binding.kind === 'gamepad' && env.getGamepads) {
    const pads = env.getGamepads;
    let was = false;
    let id = 0;
    const tick = () => {
      const down = pads().some(
        (p) =>
          !!p &&
          (binding.gamepadId === null || p.id === binding.gamepadId) &&
          !!p.buttons[binding.button]?.pressed,
      );
      if (down && !was) onPress();
      was = down;
      id = env.raf(tick);
    };
    id = env.raf(tick);
    cleanups.push(() => env.caf(id));
  }

  if (binding.kind === 'hid' && env.hid) {
    let active = true;
    let detach: (() => void) | null = null;
    void env.hid
      .getDevices()
      .then(async (devices) => {
        const dev = devices.find(
          (d) => d.vendorId === binding.vendorId && d.productId === binding.productId,
        );
        if (!dev || !active) return;
        if (!dev.opened) {
          try {
            await dev.open();
          } catch {
            return; // in use by another program; the keyboard pedal still works
          }
        }
        // Stopped while the device was opening: attach nothing.
        if (!active) return;
        let was = false;
        const onReport = (e: { data: DataView }) => {
          let down = false;
          for (let i = 0; i < e.data.byteLength; i++) if (e.data.getUint8(i) !== 0) down = true;
          if (down && !was) onPress();
          was = down;
        };
        dev.addEventListener('inputreport', onReport);
        detach = () => dev.removeEventListener('inputreport', onReport);
      })
      .catch(() => undefined);
    cleanups.push(() => {
      active = false;
      detach?.();
    });
  }

  return () => {
    for (const c of cleanups) c();
  };
}

/**
 * "Press your pedal now": resolves with the first key or gamepad button
 * pressed, or null on Escape / timeout. Call `cancel` to stop early.
 */
export function learnTrigger(
  env: TriggerEnv = browserTriggerEnv(),
  timeoutMs = 15_000,
): { result: Promise<PedalBinding | null>; cancel: () => void } {
  let finish: (b: PedalBinding | null) => void = () => undefined;
  const result = new Promise<PedalBinding | null>((resolve) => {
    finish = resolve;
  });
  let raf = 0;
  const onKey = (ev: Event) => {
    const e = ev as KeyboardEvent;
    if (e.key === 'Escape') {
      e.preventDefault();
      done(null);
      return;
    }
    if (NOT_LEARNABLE.has(e.key)) return;
    e.preventDefault();
    e.stopPropagation();
    done({ kind: 'key', code: e.code, key: e.key });
  };
  // Buttons already held when learning starts do not count.
  const held = new Set<string>();
  const pads = env.getGamepads;
  if (pads) {
    for (const p of pads()) p?.buttons.forEach((b, i) => b.pressed && held.add(`${p.id}#${i}`));
  }
  const poll = () => {
    if (!pads) return;
    for (const p of pads()) {
      if (!p) continue;
      const i = p.buttons.findIndex((b, k) => b.pressed && !held.has(`${p.id}#${k}`));
      if (i >= 0) {
        done({ kind: 'gamepad', button: i, gamepadId: p.id });
        return;
      }
    }
    raf = env.raf(poll);
  };
  const timer = setTimeout(() => done(null), timeoutMs);
  let finished = false;
  function done(b: PedalBinding | null) {
    if (finished) return;
    finished = true;
    clearTimeout(timer);
    env.target.removeEventListener('keydown', onKey, true);
    if (pads) env.caf(raf);
    finish(b);
  }
  env.target.addEventListener('keydown', onKey, true);
  if (pads) raf = env.raf(poll);
  return { result, cancel: () => done(null) };
}

/** Asks the user to pick a USB button device (WebHID; needs a click). */
export async function pickHidPedal(
  env: TriggerEnv = browserTriggerEnv(),
): Promise<PedalBinding | null> {
  if (!env.hid) return null;
  const [dev] = await env.hid.requestDevice({ filters: [] });
  if (!dev) return null;
  return {
    kind: 'hid',
    vendorId: dev.vendorId,
    productId: dev.productId,
    name: dev.productName || 'USB device',
  };
}
